'use client';

import React, { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import ShopPageReplica from '@/components/shop/ShopPageReplica';
import BrandLogo from '@/components/BrandLogo';
import { getCountry, getAllCountries } from '@/lib/GeoGraph';
import { generateProductSlug, getProductPath, generateSellerSlug } from '@/lib/catalog-helpers';
import {
  Home,
  MapPin,
  Store,
  ShieldCheck,
  ChevronDown,
  Phone,
  MessageCircle,
  X,
  Car,
  Building2,
  Navigation
} from 'lucide-react';
import CategoryCarouselRail from '@/components/CategoryCarouselRail';
import MarketplaceFooter from '@/components/MarketplaceFooter';

interface ShopCountryClientProps {
  countrySlug: string;
}

export default function ShopCountryClient({ countrySlug }: ShopCountryClientProps) {
  const router = useRouter();
  const country = getCountry(countrySlug);
  const countryCode = country.code; // 'NG' | 'GH'

  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountryCode, setSelectedCountryCode] = useState(countryCode);
  const [selectedCity, setSelectedCity] = useState<string>('all');
  const [selectedHub, setSelectedHub] = useState<string>('all');
  const [selectedVendor, setSelectedVendor] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [deviceLocation, setDeviceLocation] = useState<{ city?: string; region?: string; country?: string }>({});

  // Read ?vendor=, ?seller=, or ?category= query params safely on client
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const v = params.get('vendor') || params.get('seller');
      if (v) {
        setSelectedVendor(v.trim());
      }
      const c = params.get('category');
      if (c) {
        setSelectedCategory(c.trim().toLowerCase());
      }
    }
  }, []);

  const handleCategorySelect = (categoryId: string) => {
    const nextCat = selectedCategory.toLowerCase() === categoryId.toLowerCase() ? 'all' : categoryId;
    setSelectedCategory(nextCat);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (nextCat === 'all') {
        url.searchParams.delete('category');
      } else {
        url.searchParams.set('category', nextCat);
      }
      window.history.replaceState({}, '', url.toString());
    }
  };

  // 1. Device Geo-IP Lock & Verification
  useEffect(() => {
    let isMounted = true;
    async function verifyDeviceLocation() {
      try {
        const apiBase = process.env.NEXT_PUBLIC_P4D_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '';
        const res = await fetch(`${apiBase}/api/v1/geo/me`, { credentials: 'include' });
        const geo = await res.json();
        if (isMounted && geo?.success && geo.countrySlug) {
          setDeviceLocation({ city: geo.city, region: geo.region, country: geo.country });
          const deviceCountry = geo.countrySlug.toLowerCase();
          // Total Geo-IP Lock: If a Ghanaian device is on /ng/shop, redirect to /gh/shop
          // If a Nigerian device is on /gh/shop, redirect to /ng/shop
          if (deviceCountry !== country.slug && (deviceCountry === 'gh' || deviceCountry === 'ng')) {
            router.replace(`/${deviceCountry}/shop`);
          }
        }
      } catch (err) {
        // Fail-open for client-side geolocation checks
      }
    }
    verifyDeviceLocation();
    return () => {
      isMounted = false;
    };
  }, [country.slug, router]);

  // 2. Load Products Strictly for this Country (Zero cross-country leakage)
  useEffect(() => {
    let isMounted = true;
    async function loadProducts() {
      setLoading(true);
      try {
        const apiBase = process.env.NEXT_PUBLIC_P4D_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '';
        const res = await fetch(`${apiBase}/api/v1/marketplace/products?country=${countryCode}`, {
          credentials: 'include'
        });
        const json = await res.json();
        if (isMounted) {
          if (json?.success && Array.isArray(json.data)) {
            setProducts(json.data);
          } else {
            setProducts([]);
          }
        }
      } catch (e) {
        console.warn('Failed to load shop products:', e);
        if (isMounted) setProducts([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadProducts();
    return () => {
      isMounted = false;
    };
  }, [countryCode]);

  const availableCities = countryCode === 'GH'
    ? [
        { id: 'all', name: 'All Ghana Cities' },
        { id: 'accra', name: 'Accra' },
        { id: 'kumasi', name: 'Kumasi' },
        { id: 'tema', name: 'Tema' },
        { id: 'takoradi', name: 'Takoradi' }
      ]
    : [
        { id: 'all', name: 'All Nigeria Cities' },
        { id: 'lagos', name: 'Lagos' },
        { id: 'abuja', name: 'Abuja (FCT)' },
        { id: 'port-harcourt', name: 'Port Harcourt' },
        { id: 'kano', name: 'Kano' },
        { id: 'nnewi', name: 'Nnewi' }
      ];

  const availableHubs = countryCode === 'GH'
    ? [
        { id: 'all', name: 'All Commercial Hubs' },
        { id: 'abossey-okai', name: 'Abossey Okai Market (Accra)' },
        { id: 'suame-magazine', name: 'Suame Magazine (Kumasi)' },
        { id: 'tema-industrial', name: 'Tema Industrial Area' },
        { id: 'takoradi-hub', name: 'Takoradi Auto Enclave' }
      ]
    : [
        { id: 'all', name: 'All Commercial Hubs' },
        { id: 'ladipo', name: 'Ladipo International Market (Lagos)' },
        { id: 'aspamda', name: 'ASPAMDA Trade Fair Complex (Lagos)' },
        { id: 'apo-mechanic', name: 'Apo Mechanic Village (Abuja)' },
        { id: 'nnewi', name: 'Nkwo Nnewi Auto Spares' },
        { id: 'kano-kofar', name: 'Kofar Ruwa Auto Market' }
      ];

  // 3. Strict Country, City, Hub, and Vendor Filtering
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      // Strict country filter
      const pCountry = (p.country || p.vendor?.country || (p.sku?.includes('-GH-') ? 'GH' : 'NG')).toUpperCase();
      if (pCountry !== countryCode) return false;

      // City filter
      if (selectedCity !== 'all') {
        const pCity = (p.vendor?.city || p.vendor?.location || p.vendor?.shopLocation || p.city || '').toLowerCase();
        if (!pCity.includes(selectedCity.toLowerCase())) return false;
      }

      // Hub filter
      if (selectedHub !== 'all') {
        const pHub = (p.vendor?.hubMarket || p.vendor?.location || p.vendor?.shopLocation || p.hub || '').toLowerCase();
        const hubQuery = selectedHub.replace(/-/g, ' ').toLowerCase();
        if (!pHub.includes(hubQuery) && !pHub.includes(selectedHub)) return false;
      }

      // Vendor filter
      if (selectedVendor !== 'all') {
        const vQuery = selectedVendor.toLowerCase();
        const pVendorSlug = (p.vendor?.storeSlug || p.vendor?.slug || p.seller?.storeSlug || p.seller?.slug || '').toLowerCase();
        const pVendorName = (p.vendor?.name || p.vendor?.storeName || p.seller?.storeName || p.seller?.name || '').toLowerCase();
        const pSellerId = (p.vendor?.id || p.seller?.id || p.seller_id || '').toLowerCase();
        if (pVendorSlug !== vQuery && !pVendorName.includes(vQuery) && pSellerId !== vQuery) {
          return false;
        }
      }

      return true;
    });
  }, [products, countryCode, selectedCity, selectedHub, selectedVendor]);

  const handleProductClick = (product: any) => {
    const slug = product.slug || generateProductSlug({
      make: product.make,
      model: product.model,
      year: product.year,
      title: product.title || product.name,
      id: product.id,
      sku: product.sku
    });
    router.push(getProductPath({ country: country.slug, slug }));
  };

  const handleVendorClick = (vendor: any) => {
    const sName = vendor?.storeName || vendor?.name || 'merchant';
    const sSlug = vendor?.storeSlug || vendor?.slug || generateSellerSlug(sName);
    router.push(`/${country.slug}/sellers/${sSlug}`);
  };

  const handleCountrySwitch = (newCode: string) => {
    if (newCode.toLowerCase() === country.slug) return;
    router.push(`/${newCode.toLowerCase()}/shop`);
  };

  return (
    <div className="min-h-screen bg-[#F4F8FF] text-slate-800 flex flex-col font-sans antialiased">
      {/* Top Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/80 px-4 sm:px-6 lg:px-8 py-3.5 shadow-xs">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 sm:gap-8">
            <Link href={`/${country.slug}`} className="cursor-pointer select-none shrink-0" title={`Parts4Drive ${country.name}`} aria-label={`Parts4Drive ${country.name} Home`}>
              <BrandLogo theme="light" priority className="h-8 sm:h-9 w-auto" />
            </Link>

            <nav className="hidden md:flex items-center gap-2 text-xs font-semibold text-slate-600">
              <Link
                href={`/${country.slug}`}
                className="px-3 py-1.5 rounded-lg hover:text-[#0A1B4A] hover:bg-slate-100 transition-colors flex items-center gap-1.5"
              >
                <Home className="size-3.5 text-blue-600" />
                <span>Home</span>
              </Link>
              <Link
                href={`/${country.slug}/shop`}
                className="px-3 py-1.5 rounded-lg bg-[#0A1B4A] text-white font-bold transition-colors flex items-center gap-1.5 shadow-xs"
              >
                <span>Shop Directory</span>
              </Link>
              <Link
                href={`/${country.slug}/hubs`}
                className="px-3 py-1.5 rounded-lg hover:text-[#0A1B4A] hover:bg-slate-100 transition-colors flex items-center gap-1.5"
              >
                <MapPin className="size-3.5 text-emerald-600" />
                <span>Commercial Hubs</span>
              </Link>
              <Link
                href={`/${country.slug}/sellers`}
                className="px-3 py-1.5 rounded-lg hover:text-[#0A1B4A] hover:bg-slate-100 transition-colors flex items-center gap-1.5"
              >
                <Store className="size-3.5 text-amber-600" />
                <span>Verified Sellers</span>
              </Link>
            </nav>
          </div>

          {/* Right Action: Country Badge, WhatsApp Concierge & Sell on Parts4Drive */}
          <div className="flex items-center gap-1.5 sm:gap-3">
            <Link
              href="/signup?role=merchant"
              className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-bold sm:font-semibold text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-2 sm:px-3 py-1.5 rounded-full border border-emerald-200 transition-colors shrink-0"
              title="Sell on Parts4Drive — Join as a verified vendor"
            >
              <Store className="size-3 sm:size-3.5 text-emerald-600 shrink-0" />
              <span>Sell on <span className="hidden sm:inline">Parts4Drive</span><span className="sm:hidden">P4D</span></span>
            </Link>

            {/* Country Selector */}
            <div className="relative inline-flex items-center bg-slate-100 border border-slate-200 rounded-full px-2 sm:px-2.5 py-1 text-xs text-slate-700">
              <span className="font-bold text-blue-600 mr-1.5">{country.code}</span>
              <select
                aria-label="Switch country marketplace"
                value={country.code}
                onChange={(e) => handleCountrySwitch(e.target.value)}
                className="bg-transparent text-slate-800 text-xs font-semibold focus:outline-hidden cursor-pointer"
              >
                <option value="NG" className="bg-white text-slate-800">Nigeria (NGN ₦)</option>
                <option value="GH" className="bg-white text-slate-800">Ghana (GHS GH₵)</option>
              </select>
            </div>

            <a
              href={`https://wa.me/${country.code === 'GH' ? '233539572421' : '2348000000000'}?text=${encodeURIComponent(`Hello Parts4Drive ${country.name}, I am looking for genuine auto parts with verified fitment guarantee.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 rounded-full bg-[#25D366] hover:bg-[#20ba59] text-white font-bold text-xs shadow-xs transition-colors"
            >
              <MessageCircle className="size-3.5 fill-white text-[#25D366]" />
              <span className="hidden sm:inline">Parts Concierge</span>
            </a>
          </div>
        </div>
      </header>

      {/* Breadcrumb banner */}
      <div className="bg-white border-b border-slate-200/80 px-4 sm:px-8 py-3">
        <div className="max-w-[1600px] mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 overflow-x-auto py-0.5 text-xs text-slate-500">
              <Link href="/" className="hover:text-blue-600 transition-colors">Parts4Drive</Link>
              <span className="text-slate-400 select-none font-bold">&gt;</span>
              <Link href={`/${country.slug}`} className="hover:text-blue-600 transition-colors">{country.name}</Link>
              <span className="text-slate-400 select-none font-bold">&gt;</span>
              <span className="text-[#0A1B4A] font-bold">Shop Catalog</span>
            </nav>
            <h1 className="text-lg sm:text-xl font-black text-[#0A1B4A] tracking-tight mt-1">
              {country.name} Auto Spare Parts Catalog &amp; Verified Inventory
            </h1>
          </div>
          <div className="hidden md:flex items-center gap-2 text-[11px] text-slate-500 font-medium shrink-0">
            <ShieldCheck className="size-3.5 text-emerald-600" />
            <span>100% Exact Car Fitment Guaranteed across {country.name}</span>
          </div>
        </div>
      </div>

      {/* Local Market & Commercial Hub Dispatch Bar */}
      <div className="bg-slate-50/80 border-b border-slate-200/80 px-4 sm:px-8 py-3">
        <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-50 border border-blue-200 text-blue-700 font-bold">
              <Navigation className="size-3 text-blue-600" />
              <span>Geo-Locked: {country.name} ({country.currency})</span>
            </span>

            {/* City Selector */}
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2.5 py-1 text-slate-700 shadow-2xs">
              <MapPin className="size-3.5 text-emerald-600 shrink-0" />
              <label htmlFor="shop-city-select" className="sr-only">Select City</label>
              <select
                id="shop-city-select"
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="bg-transparent text-slate-800 text-xs font-semibold focus:outline-hidden cursor-pointer"
              >
                {availableCities.map((c) => (
                  <option key={c.id} value={c.id} className="bg-white text-slate-800">
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Commercial Hub Selector */}
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2.5 py-1 text-slate-700 shadow-2xs">
              <Building2 className="size-3.5 text-amber-600 shrink-0" />
              <label htmlFor="shop-hub-select" className="sr-only">Select Commercial Hub</label>
              <select
                id="shop-hub-select"
                value={selectedHub}
                onChange={(e) => setSelectedHub(e.target.value)}
                className="bg-transparent text-slate-800 text-xs font-semibold focus:outline-hidden cursor-pointer"
              >
                {availableHubs.map((h) => (
                  <option key={h.id} value={h.id} className="bg-white text-slate-800">
                    {h.name}
                  </option>
                ))}
              </select>
            </div>

            {(selectedCity !== 'all' || selectedHub !== 'all') && (
              <button
                type="button"
                onClick={() => {
                  setSelectedCity('all');
                  setSelectedHub('all');
                }}
                className="px-2.5 py-1 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 font-semibold text-[11px] transition-colors cursor-pointer"
              >
                Reset Location
              </button>
            )}

            {selectedVendor !== 'all' && (
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-xl bg-blue-50 border border-blue-200 text-blue-700 text-xs font-bold">
                <Store className="size-3.5" />
                <span>Vendor: <strong className="text-[#0A1B4A]">{selectedVendor}</strong></span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedVendor('all');
                    if (typeof window !== 'undefined') {
                      const url = new URL(window.location.href);
                      url.searchParams.delete('vendor');
                      url.searchParams.delete('seller');
                      window.history.replaceState({}, '', url.toString());
                    }
                  }}
                  className="ml-1 p-0.5 rounded-full hover:bg-slate-200 text-slate-500 hover:text-slate-800 transition-colors"
                  title="Clear Vendor Filter"
                >
                  <X className="size-3" />
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 text-slate-500 text-[11px]">
            <span>Showing <strong className="text-[#0A1B4A] font-bold">{filteredProducts.length}</strong> verified parts</span>
            <span className="hidden lg:inline">•</span>
            <span className="hidden lg:inline">Verified dispatch hubs: {country.hubs.map(h => h.name).join(', ')}</span>
          </div>
        </div>
      </div>


      {/* Category Quick Rail */}
      <div className="max-w-[1600px] w-full mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-2">
        <CategoryCarouselRail
          country={country.slug}
          activeCategory={selectedCategory}
          onSelectCategory={handleCategorySelect}
        />
      </div>

      {/* Catalog Matrix */}
      <main className="flex-1 max-w-[1600px] w-full mx-auto px-2 sm:px-4 lg:px-6 py-4">
        <ShopPageReplica
          products={filteredProducts}
          isLoading={loading}
          selectedCountry={country.code}
          onProductClick={handleProductClick}
          onVendorClick={handleVendorClick}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategorySelect}
        />
      </main>

      {/* Standardized Marketplace Official Footer */}
      <MarketplaceFooter country={country.slug} />
    </div>
  );
}
